import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Info, Check } from 'lucide-react'
import toast from 'react-hot-toast'
// La entrega a producción vive en la COTIZACIÓN (es ahí donde se guarda su estado), pero se
// opera desde la ficha del cliente.
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import DatosAlEntregar from '@/components/cd2b/DatosAlEntregar'
import { mensajeDeError } from '@/lib/errores'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

/* ─────────────────── Entrega a producción ───────────────────
 *
 * EL PASO QUE NO SE VEÍA. La empresa de prueba y la real son el MISMO inquilino: promover no
 * mueve datos, solo voltea `esDemo` (`promoverAReal`). Pero la única pantalla que lo ofrecía
 * era el editor de la COTIZACIÓN, y encima solo cuando ya estaba pagada — así que el asesor,
 * que después de vender trabaja desde la ficha del cliente, no tenía forma de llegar.
 *
 * Tres decisiones sobre cómo se enseña, y las tres van contra ocultar cosas:
 *
 *   1. La tarjeta se pinta SIEMPRE que la empresa sea de prueba, pagada o no. Antes, sin pago
 *      la tarjeta entera desaparecía y no había manera de ver qué faltaba; ahora el pago sale
 *      como un requisito más de la lista, que es lo que es.
 *   2. Los requisitos se piden al servidor (`chequeoProduccion`), no se adivinan aquí. Es el
 *      mismo chequeo que ejecuta la entrega, así que la pantalla no puede prometer algo que
 *      luego el servidor rechace.
 *   3. El botón vive deshabilitado hasta que el servidor dice `listo`. No se habilita «por si
 *      acaso»: entregar una empresa con el NIT del giro es peor que no entregarla.
 *
 * No duplica lógica: reusa los endpoints que ya existían para la cotización.
 */
export default function EntregaAProduccion({ ficha, onCambio, sinDemo = false }) {
  const navigate = useNavigate()
  const propuestaId = ficha.propuestaOrigen?.id || null
  const [chequeo, setChequeo] = useState(null)
  const [trabajando, setTrabajando] = useState(false)
  // Los bloqueos de datos (país, giro, emisor) se resuelven en el paso 1 de la cotización. El
  // botón lleva directo ahí —y a la identidad fiscal si es el emisor—, no a «Revisar y enviar»,
  // donde no hay nada que editar y el asesor no sabía a dónde ir.
  const codigos = (chequeo?.bloqueos || []).map((b) => b.code)
  const faltaEmisor = codigos.some((c) => String(c).startsWith('EMISOR_'))
  const faltaDato = faltaEmisor || codigos.some((c) => ['SIN_GIRO', 'PAIS_REQUERIDO'].includes(c))
  // El catálogo del contador NO está en el paso 1: está en «Revisar y enviar» (paso 4).
  const faltaCatalogo = codigos.some((c) => String(c).startsWith('CATALOGO_'))
  const destino = faltaDato
    ? `?paso=1${faltaEmisor ? '&seccion=identidad-fiscal' : ''}`
    : faltaCatalogo
      ? '?paso=4&seccion=catalogo-contador'
      : ''
  const rutaCotizacion = `/consultor/cotizaciones/${propuestaId}${destino}`

  const revisar = useCallback(async () => {
    if (!propuestaId) return
    setTrabajando(true)
    try {
      const r = await cd2bPropuestaService.listoParaProduccion(propuestaId)
      setChequeo(r?.data || null)
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudieron leer los requisitos'))
    } finally { setTrabajando(false) }
  }, [propuestaId])

  // Se consultan al abrir la pestaña, no al pulsar un botón: si hay algo que resolver, el
  // asesor tiene que verlo mientras trabaja la empresa, no cuando ya viene a entregarla.
  useEffect(() => { revisar() }, [revisar])

  const entregar = async () => {
    setTrabajando(true)
    try {
      await cd2bPropuestaService.pasarAProduccion(propuestaId)
      toast.success(sinDemo ? 'Entregada. Ya tiene su espacio en la nube: puedes ver el acceso del dueño.' : 'Entregada. La empresa ya es real: los datos de prueba dejan de ofrecerse.')
      onCambio?.()
    } catch (e) {
      const data = e?.response?.data
      // `chequeoProduccion` devuelve los bloqueos uno a uno; enseñarlos es la mitad del valor.
      if (data?.bloqueos?.length) {
        setChequeo({ listo: false, bloqueos: data.bloqueos, advertencias: [] })
        toast.error('Faltan requisitos. Revisa la lista.')
      } else {
        toast.error(mensajeDeError(e, 'No se pudo entregar la empresa'))
      }
    } finally { setTrabajando(false) }
  }

  // Sin cotización de origen no hay dónde guardar el estado de la entrega, igual que pasa con
  // la migración. Se dice, en vez de enseñar un botón que no puede funcionar.
  if (!propuestaId) {
    return (
      <Card className="p-4 sm:p-5">
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-1">Entregar a producción</h3>
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          Esta empresa no nació de una cotización, así que la entrega no se gestiona desde aquí.
          Sigue siendo de prueba hasta que alguien la convierta desde el panel de administración.
        </p>
      </Card>
    )
  }

  return (
    <Card className="p-4 sm:p-5 space-y-3">
      <div>
        <h3 className="font-semibold text-neutral-900 dark:text-neutral-50">Entregar a producción</h3>
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          {sinDemo
            ? 'Esta empresa se pagó sin pasar por una demo. Al entregarla se crea su espacio en la nube con los datos del emisor y, si el cliente trae el suyo, con el catálogo de cuentas de su contador; desde ese momento ya hay acceso del dueño y accesos por puesto.'
            : 'La empresa de prueba y la real son la misma: no se migra nada, se entrega. Conserva su catálogo, su configuración y todo lo que el dueño lleve hecho. Lo que cambia es que deja de ser una prueba — y los datos de ejemplo dejan de ofrecerse.'}
        </p>
      </div>

      {chequeo?.yaEnProduccion && (
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">Esta empresa ya está entregada.</p>
      )}

      {!chequeo?.yaEnProduccion && (
        <>
          {/* Con todo, sin los datos de ejemplo o vacía: la decisión vive en la cotización y es
              requisito de la entrega, así que se toma aquí mismo, junto al botón que entrega. */}
          <DatosAlEntregar propuestaId={propuestaId} onCambio={revisar} />

          {chequeo?.bloqueos?.length > 0 && (
            <div className="space-y-1">
              <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">Falta por resolver</p>
              <ul className="space-y-1">
                {chequeo.bloqueos.map((b) => (
                  <li key={b.code} className="flex items-start gap-2 text-body-sm text-danger-700 dark:text-danger-300">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{b.mensaje}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {chequeo?.advertencias?.length > 0 && (
            <div className="space-y-1">
              <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50">Conviene revisar</p>
              <ul className="space-y-1">
                {chequeo.advertencias.map((a) => (
                  <li key={a.code} className="flex items-start gap-2 text-body-sm text-neutral-600 dark:text-neutral-400">
                    <Info size={14} className="mt-0.5 shrink-0" />
                    <span>{a.mensaje}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {chequeo?.listo && (
            <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
              Todo en orden. Al entregarla, el dueño conserva sus accesos y su empresa deja de ser una prueba.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={entregar} disabled={trabajando || !chequeo?.listo}>
              <Check size={15} className="mr-1.5" /> Entregar a producción
            </Button>
            <Button variant="outline" onClick={revisar} disabled={trabajando}>
              Volver a revisar
            </Button>
            <Button variant={faltaDato || faltaCatalogo ? 'outline' : 'ghost'} onClick={() => navigate(rutaCotizacion)}>
              {faltaEmisor
                ? 'Completar identidad fiscal'
                : faltaDato
                  ? 'Completar datos en la cotización'
                  : faltaCatalogo
                    ? 'Revisar el catálogo de cuentas'
                    : 'Abrir su cotización'}
            </Button>
          </div>
        </>
      )}
    </Card>
  )
}
